/* ============================================================
   COLLECTION AKHAVAN — Custom date picker
   Replaces the native <input type="date"> so the calendar popup
   can be brand-styled (rounded corners, TT Corals, light/dark).
   Submits yyyy-mm-dd via a hidden input, exactly like the native
   control did; the visible field shows the locale's date format.

   Copy and date conventions (month names, weekday order, display
   format) arrive as `window.akDatepickerStrings`, populated from
   `ak.datepicker.*` in locales/*.json by theme.liquid. The English
   values below are only a fallback for when that block is absent.
   ============================================================ */

(function () {
  'use strict';

  const DEFAULTS = {
    months: ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'],
    weekdays: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
    firstDay: 0,
    dateFormat: 'mm/dd/yyyy',
    today: 'Today',
    earliest: 'Earliest',
    clear: 'Clear',
    dialogLabel: 'Choose date',
    prevMonth: 'Previous month',
    nextMonth: 'Next month'
  };
  const STR = Object.assign({}, DEFAULTS, window.akDatepickerStrings || {});

  const MONTHS = STR.months;

  /* Which weekday the grid starts on: 0 = Sunday (en), 1 = Monday (de).
     The header labels rotate to match, so column N always means the same day. */
  const FIRST_DAY = ((Number(STR.firstDay) || 0) % 7 + 7) % 7;
  const DOW = STR.weekdays.slice(FIRST_DAY).concat(STR.weekdays.slice(0, FIRST_DAY));

  const pad = n => String(n).padStart(2, '0');
  const toISO = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  /* Token substitution rather than a fixed order, so 'dd.mm.yyyy' and
     'mm/dd/yyyy' both come out of the same locale-supplied string. */
  const toDisplay = d => STR.dateFormat
    .replace('yyyy', d.getFullYear())
    .replace('mm', pad(d.getMonth() + 1))
    .replace('dd', pad(d.getDate()));
  const sameDay = (a, b) => a && b &&
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  /* Translated copy is interpolated into an innerHTML string below. */
  const esc = s => String(s).replace(/[&<>"]/g, c =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  function initPicker(root) {
    const field  = root.querySelector('.ak-dp__field');
    const hidden = root.querySelector('[data-datepicker-value]');
    const icon   = root.querySelector('.ak-dp__icon');
    if (!field || !hidden) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    /* Earliest selectable date. Lead time (set in the theme editor) pushes
       the minimum forward by N days; 0 = today is bookable. */
    const leadDays = parseInt(root.dataset.leadDays, 10) || 0;
    const minDate = new Date(today);
    minDate.setDate(minDate.getDate() + leadDays);

    /* Restore a pre-filled value (yyyy-mm-dd) if the form re-rendered with one. */
    let selected = null;
    if (hidden.value) {
      const parts = hidden.value.split('-');
      if (parts.length === 3) {
        selected = new Date(+parts[0], +parts[1] - 1, +parts[2]);
        field.value = toDisplay(selected);
      }
    }

    let viewYear  = (selected || today).getFullYear();
    let viewMonth = (selected || today).getMonth();

    /* ---- Build popup shell ---- */
    const pop = document.createElement('div');
    pop.className = 'ak-dp__pop';
    pop.setAttribute('role', 'dialog');
    pop.setAttribute('aria-label', STR.dialogLabel);
    pop.innerHTML =
      '<div class="ak-dp__head">' +
        '<span class="ak-dp__title"></span>' +
        '<div class="ak-dp__nav">' +
          '<button class="ak-dp__navbtn" type="button" data-prev aria-label="' + esc(STR.prevMonth) + '">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"></polyline></svg>' +
          '</button>' +
          '<button class="ak-dp__navbtn" type="button" data-next aria-label="' + esc(STR.nextMonth) + '">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"></polyline></svg>' +
          '</button>' +
        '</div>' +
      '</div>' +
      '<div class="ak-dp__dow">' + DOW.map(d => '<span>' + esc(d) + '</span>').join('') + '</div>' +
      '<div class="ak-dp__grid"></div>' +
      '<div class="ak-dp__foot">' +
        '<button class="ak-dp__link" type="button" data-clear></button>' +
        '<button class="ak-dp__link" type="button" data-today></button>' +
      '</div>';
    pop.querySelector('[data-clear]').textContent = STR.clear;
    root.appendChild(pop);

    const title = pop.querySelector('.ak-dp__title');
    const grid  = pop.querySelector('.ak-dp__grid');

    /* ---- Render the current month grid (always 6 rows) ---- */
    function render() {
      title.textContent = MONTHS[viewMonth] + ' ' + viewYear;

      /* Leading blanks before the 1st, counted from the locale's first weekday
         rather than always from Sunday. */
      const startDow    = (new Date(viewYear, viewMonth, 1).getDay() - FIRST_DAY + 7) % 7;
      const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

      grid.innerHTML = '';
      for (let i = 0; i < 42; i++) {
        let dayNum, offset;
        if (i < startDow) {
          dayNum = new Date(viewYear, viewMonth, 0).getDate() - (startDow - 1 - i);
          offset = -1;
        } else if (i >= startDow + daysInMonth) {
          dayNum = i - (startDow + daysInMonth) + 1;
          offset = 1;
        } else {
          dayNum = i - startDow + 1;
          offset = 0;
        }
        const cellDate = new Date(viewYear, viewMonth + offset, dayNum);

        const isBlocked = cellDate < minDate;

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ak-dp__day';
        if (offset !== 0) btn.classList.add('ak-dp__day--muted');
        if (sameDay(cellDate, today)) btn.classList.add('ak-dp__day--today');
        if (sameDay(cellDate, selected)) btn.classList.add('ak-dp__day--selected');
        if (isBlocked) {
          btn.classList.add('ak-dp__day--disabled');
          btn.disabled = true;
        } else {
          btn.addEventListener('click', () => pick(cellDate));
        }
        btn.textContent = dayNum;
        grid.appendChild(btn);
      }
    }

    function pick(date) {
      if (date < minDate) return;
      selected  = date;
      viewYear  = date.getFullYear();
      viewMonth = date.getMonth();
      field.value  = toDisplay(date);
      hidden.value = toISO(date);
      field.dispatchEvent(new Event('change', { bubbles: true }));
      close();
    }

    /* ---- Open / close ---- */
    function open() {
      if (root.classList.contains('open')) return;
      render();
      /* Flip upward when there isn't room for the popup below the field. */
      const rect = field.getBoundingClientRect();
      const popH = pop.offsetHeight;
      const roomBelow = window.innerHeight - rect.bottom;
      root.classList.toggle('ak-dp--up', roomBelow < popH + 16 && rect.top > popH + 16);
      root.classList.add('open');
      field.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', onOutside, true);
      document.addEventListener('keydown', onKey);
    }
    function close() {
      if (!root.classList.contains('open')) return;
      root.classList.remove('open');
      field.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', onOutside, true);
      document.removeEventListener('keydown', onKey);
    }
    function toggle() { root.classList.contains('open') ? close() : open(); }

    function onOutside(e) { if (!root.contains(e.target)) close(); }
    function onKey(e) { if (e.key === 'Escape') { close(); field.focus(); } }

    /* ---- Wire controls ---- */
    field.addEventListener('click', toggle);
    field.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') { e.preventDefault(); open(); }
    });
    icon && icon.addEventListener('click', toggle);

    pop.querySelector('[data-prev]').addEventListener('click', () => {
      if (--viewMonth < 0) { viewMonth = 11; viewYear--; }
      render();
    });
    pop.querySelector('[data-next]').addEventListener('click', () => {
      if (++viewMonth > 11) { viewMonth = 0; viewYear++; }
      render();
    });
    /* Shortcut to the soonest bookable date. With no lead time that's today;
       with lead time it's today + leadDays, so relabel it accordingly. */
    const soonBtn = pop.querySelector('[data-today]');
    soonBtn.textContent = leadDays > 0 ? STR.earliest : STR.today;
    soonBtn.addEventListener('click', () => pick(new Date(minDate)));
    pop.querySelector('[data-clear]').addEventListener('click', () => {
      selected = null;
      field.value = '';
      hidden.value = '';
      field.dispatchEvent(new Event('change', { bubbles: true }));
      close();
    });
  }

  document.querySelectorAll('[data-datepicker]').forEach(initPicker);
})();